import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import ReadinessGauge from "@/components/ReadinessGauge";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, Check, Building2, BadgeCheck, ShieldCheck, HardHat, BarChart3, Briefcase, Plus, X } from "lucide-react";

const TRADES = [
  ["general", "General Contractor"], ["electrical", "Electrical"], ["hvac", "HVAC"],
  ["plumbing", "Plumbing"], ["roofing", "Roofing"], ["concrete", "Concrete"],
  ["civil", "Civil"], ["specialty", "Specialty"],
];
const CERTS = ["8(a)", "MBE", "WBE", "SDVOSB", "HUBZone", "DBE", "ISO 9001", "VOSB"];

const STEPS = [
  { key: "company", label: "Company", icon: Building2 },
  { key: "licensing", label: "Licensing", icon: BadgeCheck },
  { key: "bonding", label: "Bonding", icon: ShieldCheck },
  { key: "safety", label: "Safety", icon: HardHat },
  { key: "experience", label: "Experience", icon: BarChart3 },
  { key: "capabilities", label: "Capabilities", icon: Briefcase },
];

const inputCls = "rounded-md border border-slate-200 px-3 py-2 text-sm w-full focus:outline-none focus:ring-2 focus:ring-accent/40 focus:border-accent";

const Field = ({ label, children }) => (
  <label className="flex flex-col gap-1.5">
    <span className="text-sm font-medium text-slate-700">{label}</span>
    {children}
  </label>
);
const Toggle = ({ checked, onChange, testid }) => (
  <button type="button" onClick={() => onChange(!checked)} data-testid={testid}
    className={`w-12 h-6 rounded-full transition-colors relative ${checked ? "bg-emerald-500" : "bg-slate-200"}`}>
    <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-all ${checked ? "left-6" : "left-0.5"}`} />
  </button>
);

export default function Profile() {
  const [p, setP] = useState(null);
  const [readiness, setReadiness] = useState({ score: 0, tier: "" });
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/profile").then((r) => { setP(r.data.profile); setReadiness(r.data.readiness); });
  }, []);

  const set = (k, v) => setP((prev) => ({ ...prev, [k]: v }));
  const toggleCert = (c) => setP((prev) => ({ ...prev, certifications: prev.certifications.includes(c) ? prev.certifications.filter((x) => x !== c) : [...prev.certifications, c] }));
  const addProject = () => set("past_projects", [...(p.past_projects || []), { title: "", client: "", value: 0, year: "" }]);
  const updProject = (i, k, v) => set("past_projects", (p.past_projects || []).map((x, idx) => (idx === i ? { ...x, [k]: v } : x)));
  const rmProject = (i) => set("past_projects", (p.past_projects || []).filter((_, idx) => idx !== i));

  const persist = async (silent = false) => {
    setSaving(true);
    try {
      const r = await api.put("/profile", p);
      setReadiness(r.data.readiness);
      if (!silent) toast.success("Profile saved — readiness updated");
      return true;
    } catch (e) { toast.error("Failed to save"); return false; }
    finally { setSaving(false); }
  };

  const next = async () => { const ok = await persist(true); if (ok) setStep((s) => Math.min(s + 1, STEPS.length - 1)); };
  const back = () => setStep((s) => Math.max(s - 1, 0));

  if (!p) return <div className="h-64 flex items-center justify-center"><div className="h-8 w-8 border-[3px] border-slate-200 border-t-slate-900 rounded-full animate-spin" /></div>;

  const pct = ((step + 1) / STEPS.length) * 100;

  return (
    <div className="space-y-6" data-testid="profile-page">
      <div>
        <h1 className="font-heading text-3xl font-bold tracking-tight">Profile Builder</h1>
        <p className="text-slate-500 mt-1">Complete each step — your readiness score updates as you go.</p>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          {/* Stepper */}
          <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-5">
            <div className="flex items-center justify-between mb-3">
              {STEPS.map((s, i) => (
                <button key={s.key} onClick={() => setStep(i)} data-testid={`step-${s.key}`} className="flex flex-col items-center gap-1.5 flex-1 group">
                  <div className={`h-9 w-9 rounded-full flex items-center justify-center transition-colors ${i < step ? "bg-emerald-500 text-white" : i === step ? "bg-emerald-900 text-white" : "bg-slate-100 text-slate-400"}`}>
                    {i < step ? <Check className="h-4 w-4" /> : <s.icon className="h-4 w-4" />}
                  </div>
                  <span className={`text-[11px] font-medium ${i === step ? "text-slate-900" : "text-slate-400"} hidden sm:block`}>{s.label}</span>
                </button>
              ))}
            </div>
            <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden">
              <div className="h-full rounded-full bg-accent transition-all duration-500" style={{ width: `${pct}%` }} />
            </div>
          </div>

          {/* Step content */}
          <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-6">
            {step === 0 && (
              <div className="grid sm:grid-cols-2 gap-4" data-testid="step-content-company">
                <Field label="Company name"><input className={inputCls} data-testid="input-company-name" value={p.company_name} onChange={(e) => set("company_name", e.target.value)} /></Field>
                <Field label="Primary trade">
                  <select className={inputCls} data-testid="select-trade" value={p.trade} onChange={(e) => set("trade", e.target.value)}>
                    <option value="">Select trade…</option>
                    {TRADES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                  </select>
                </Field>
                <Field label="State"><input className={inputCls} data-testid="input-state" value={p.state} onChange={(e) => set("state", e.target.value)} placeholder="e.g. TX" /></Field>
                <Field label="Years in business"><input type="number" className={inputCls} data-testid="input-years" value={p.years_in_business} onChange={(e) => set("years_in_business", +e.target.value)} /></Field>
                <Field label="Website"><input className={inputCls} data-testid="input-website" value={p.website || ""} onChange={(e) => set("website", e.target.value)} placeholder="https://yourcompany.com" /></Field>
                <Field label="UEI / business registration #"><input className={inputCls} data-testid="input-uei" value={p.uei || ""} onChange={(e) => set("uei", e.target.value)} placeholder="e.g. UEI or company reg. no." /></Field>
              </div>
            )}
            {step === 1 && (
              <div className="space-y-4" data-testid="step-content-licensing">
                <div className="grid sm:grid-cols-2 gap-4">
                  <Field label="Licensed?"><Toggle checked={p.has_license} onChange={(v) => set("has_license", v)} testid="toggle-license" /></Field>
                  <Field label="License number"><input className={inputCls} data-testid="input-license-number" value={p.license_number} onChange={(e) => set("license_number", e.target.value)} /></Field>
                </div>
                <div>
                  <span className="text-sm font-medium text-slate-700">Certifications</span>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {CERTS.map((c) => (
                      <button key={c} type="button" onClick={() => toggleCert(c)} data-testid={`cert-${c}`}
                        className={`text-xs font-medium rounded-full px-3 py-1.5 border transition-colors ${p.certifications.includes(c) ? "bg-emerald-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200 hover:border-slate-300"}`}>{c}</button>
                    ))}
                  </div>
                </div>
              </div>
            )}
            {step === 2 && (
              <div className="grid sm:grid-cols-2 gap-4" data-testid="step-content-bonding">
                <Field label="Bonding capacity (single project, $)"><input type="number" className={inputCls} data-testid="input-bonding" value={p.bonding_capacity} onChange={(e) => set("bonding_capacity", +e.target.value)} /></Field>
                <div />
                <Field label="General liability insurance"><Toggle checked={p.has_general_liability} onChange={(v) => set("has_general_liability", v)} testid="toggle-gl" /></Field>
                <Field label="Workers' comp"><Toggle checked={p.has_workers_comp} onChange={(v) => set("has_workers_comp", v)} testid="toggle-wc" /></Field>
              </div>
            )}
            {step === 3 && (
              <div className="grid sm:grid-cols-2 gap-4" data-testid="step-content-safety">
                <Field label="EMR (Experience Mod Rate)"><input type="number" step="0.01" className={inputCls} data-testid="input-emr" value={p.emr} onChange={(e) => set("emr", +e.target.value)} /></Field>
                <Field label="OSHA incidents (last year)"><input type="number" className={inputCls} data-testid="input-osha" value={p.osha_incidents_last_year} onChange={(e) => set("osha_incidents_last_year", +e.target.value)} /></Field>
              </div>
            )}
            {step === 4 && (
              <div className="grid sm:grid-cols-2 gap-4" data-testid="step-content-experience">
                <Field label="Gov contracts completed"><input type="number" className={inputCls} data-testid="input-gov-contracts" value={p.gov_contracts_completed} onChange={(e) => set("gov_contracts_completed", +e.target.value)} /></Field>
                <Field label="Annual revenue ($)"><input type="number" className={inputCls} data-testid="input-revenue" value={p.annual_revenue} onChange={(e) => set("annual_revenue", +e.target.value)} /></Field>
                <Field label="Employee count"><input type="number" className={inputCls} data-testid="input-employees" value={p.employee_count} onChange={(e) => set("employee_count", +e.target.value)} /></Field>
              </div>
            )}
            {step === 5 && (
              <div className="space-y-5" data-testid="step-content-capabilities">
                <div className="grid sm:grid-cols-2 gap-4">
                  <Field label="Service area / regions served"><input className={inputCls} data-testid="input-service-areas" value={p.service_areas || ""} onChange={(e) => set("service_areas", e.target.value)} placeholder="e.g. TX, OK, Southwest US" /></Field>
                  <Field label="Workforce type">
                    <select className={inputCls} data-testid="select-union" value={p.union_status || ""} onChange={(e) => set("union_status", e.target.value)}>
                      <option value="">Select…</option>
                      <option value="union">Union</option>
                      <option value="non-union">Non-union</option>
                      <option value="both">Both</option>
                    </select>
                  </Field>
                  <Field label="Licensed / key personnel"><input type="number" className={inputCls} data-testid="input-key-personnel" value={p.key_personnel || 0} onChange={(e) => set("key_personnel", +e.target.value)} /></Field>
                  <Field label="Equipment & fleet"><input className={inputCls} data-testid="input-equipment" value={p.equipment || ""} onChange={(e) => set("equipment", e.target.value)} placeholder="e.g. 3 cranes, 12 trucks, owned shop" /></Field>
                </div>
                <Field label="Client references">
                  <textarea className={`${inputCls} min-h-[80px]`} data-testid="input-references" value={p.client_references || ""} onChange={(e) => set("client_references", e.target.value)} placeholder="Name, organization, contact — one per line" />
                </Field>
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-slate-700">Past projects</span>
                    <button type="button" onClick={addProject} data-testid="add-project-button" className="inline-flex items-center gap-1 text-sm font-medium text-emerald-700 hover:text-emerald-900"><Plus className="h-4 w-4" /> Add project</button>
                  </div>
                  <div className="space-y-2">
                    {(p.past_projects || []).length === 0 && <p className="text-sm text-slate-400">No projects added yet.</p>}
                    {(p.past_projects || []).map((proj, i) => (
                      <div key={i} className="grid grid-cols-12 gap-2 items-center" data-testid={`project-row-${i}`}>
                        <input className={`${inputCls} col-span-4`} placeholder="Project title" value={proj.title || ""} onChange={(e) => updProject(i, "title", e.target.value)} data-testid={`project-title-${i}`} />
                        <input className={`${inputCls} col-span-3`} placeholder="Client" value={proj.client || ""} onChange={(e) => updProject(i, "client", e.target.value)} data-testid={`project-client-${i}`} />
                        <input type="number" className={`${inputCls} col-span-2`} placeholder="Value $" value={proj.value || 0} onChange={(e) => updProject(i, "value", +e.target.value)} data-testid={`project-value-${i}`} />
                        <input className={`${inputCls} col-span-2`} placeholder="Year" value={proj.year || ""} onChange={(e) => updProject(i, "year", e.target.value)} data-testid={`project-year-${i}`} />
                        <button type="button" onClick={() => rmProject(i)} data-testid={`remove-project-${i}`} className="col-span-1 flex justify-center text-slate-400 hover:text-red-500"><X className="h-4 w-4" /></button>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            <div className="flex items-center justify-between mt-8 pt-5 border-t border-slate-100">
              <button onClick={back} disabled={step === 0} data-testid="back-step-button" className="inline-flex items-center gap-1 text-sm font-medium text-slate-600 hover:text-slate-900 disabled:opacity-40"><ChevronLeft className="h-4 w-4" /> Back</button>
              {step < STEPS.length - 1 ? (
                <button onClick={next} data-testid="next-step-button" className="bg-emerald-900 text-white rounded-md px-5 py-2.5 text-sm font-medium hover:bg-emerald-800 transition-colors inline-flex items-center gap-1">Next <ChevronRight className="h-4 w-4" /></button>
              ) : (
                <button onClick={() => persist(false)} disabled={saving} data-testid="save-profile-button" className="bg-emerald-600 text-white rounded-md px-5 py-2.5 text-sm font-medium hover:bg-emerald-700 transition-colors inline-flex items-center gap-2 disabled:opacity-60"><Check className="h-4 w-4" /> {saving ? "Saving…" : "Finish & Save"}</button>
              )}
            </div>
          </div>
        </div>

        <div className="lg:col-span-1">
          <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-6 sticky top-6 flex flex-col items-center">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-400 self-start mb-3">Live Readiness</p>
            <ReadinessGauge score={readiness.score} tier={readiness.tier} />
            <p className="text-xs text-slate-400 text-center mt-4">Updates each time you advance a step.</p>
          </div>
        </div>
      </div>
    </div>
  );
}
